import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { PlaneTakeoff, PowerOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function formatEnd(iso) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(new Date(iso));
  } catch { return iso.slice(0, 10); }
}

/**
 * VacationBadge — Dashboard hero pill that surfaces active AI auto-reply mailboxes.
 * Hidden entirely when no mailbox is currently on vacation.
 */
export default function VacationBadge({ companyId }) {
  const [data, setData] = useState({ active_count: 0, latest_end: "", mailboxes: [] });
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/vacation-kit/status");
      setData(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, [companyId]);

  const filtered = companyId ? data.mailboxes.filter((m) => m.company_id === companyId) : data.mailboxes;
  const count = filtered.length;
  if (count === 0) return null;

  const latestEnd = filtered.reduce((max, m) => (m.ai_auto_reply_end > max ? m.ai_auto_reply_end : max), "");

  const deactivateAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/vacation-kit/deactivate-all");
      toast.success(`Deactivated on ${data.deactivated} mailbox${data.deactivated === 1 ? "" : "es"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="rounded-2xl border border-sun/40 bg-gradient-to-r from-sun/12 via-white to-emerald-brand/8 shadow-sm"
      data-testid="vacation-badge"
    >
      <div className="p-4 flex flex-wrap items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-sun/25 text-yellow-700 grid place-items-center shadow-inner">
          <PlaneTakeoff className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] font-mono uppercase tracking-widest text-yellow-700/80">Vacation Kit active</div>
          <div className="text-sm font-medium text-foreground">
            AI is auto-replying on <span className="font-mono text-yellow-700">{count}</span> mailbox{count === 1 ? "" : "es"}
            {latestEnd && <> until <span className="font-mono text-yellow-700">{formatEnd(latestEnd)}</span></>}
          </div>
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-[10px] font-mono uppercase tracking-widest text-emerald-brand hover:underline"
          data-testid="vacation-badge-toggle"
        >
          {expanded ? "Hide" : "Show"} mailboxes
        </button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="outline" className="rounded-xl text-xs border-crimson/30 text-crimson hover:bg-crimson/10" disabled={busy} data-testid="vacation-badge-deactivate-all">
              <PowerOff className="h-3.5 w-3.5 mr-1" /> Deactivate all
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-2xl" data-testid="vacation-deactivate-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display tracking-tight">Turn off all AI auto-replies?</AlertDialogTitle>
              <AlertDialogDescription>
                This immediately stops AI auto-reply on {count} mailbox{count === 1 ? "" : "es"}. You can reactivate any time from Email Accounts → Vacation Kit.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
              <AlertDialogAction className="rounded-xl bg-crimson hover:bg-crimson/90" onClick={deactivateAll}>Deactivate all</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.ul
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="border-t border-sun/30 overflow-hidden"
          >
            {filtered.map((m) => (
              <li key={m.id} className="flex items-center justify-between px-4 py-2.5 text-sm border-b border-sun/20 last:border-b-0" data-testid={`vacation-mailbox-${m.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{m.display_name || m.email_address}</div>
                  <div className="font-mono text-[10px] text-muted-foreground truncate">
                    {m.email_address}
                    {m.ai_auto_reply_timezone && m.ai_auto_reply_timezone !== "UTC" && <> · {m.ai_auto_reply_timezone}</>}
                  </div>
                </div>
                <div className="text-[10px] font-mono text-yellow-700 ml-3 shrink-0">
                  until {formatEnd(m.ai_auto_reply_end)}
                </div>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
